import { Component } from 'react';

/**
 * Top-level safety net. A render-time throw anywhere in the tree (e.g. a value
 * that isn't a valid React child) would otherwise unmount the whole app and
 * leave a blank screen. This catches it and shows a recoverable fallback.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    // Coerce to a string — the error may itself be a non-string object.
    const message =
      (error && typeof error.message === 'string' && error.message) ||
      (typeof error === 'string' ? error : 'Something went wrong');
    return { hasError: true, message };
  }

  componentDidCatch(error, info) {
    // Surface the real error + component stack in the console for debugging.
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, message: '' });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-md w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Something went wrong
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The page hit an unexpected error. Reloading usually fixes it.
          </p>
          <p className="text-xs text-gray-400 break-words font-mono">{this.state.message}</p>
          <button
            onClick={this.handleReload}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-teal-700 text-white text-sm hover:opacity-90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
